import {
  Card,
  Container,
  SectionTitle,
  DeliveryDateWrapper,
  OrderReference,
  ShippingAddress,
  ShippingWrapper,
  ShippingTitleWrapper,
  ShippingTypesWrapper,
  ShippingTypeItem,
  ShippingTypeName,
  PickupInfoNote,
  SectionTitleWrapper,
  PickupAddress,
  AdditionalCartDetails,
  AdditionalCartDetailsContent,
} from './CheckoutPage.styled';
import { useTranslation } from 'react-i18next';
import ShippingAddressCard from './ShippingAddressCard/ShippingAddressCard';
import DatePicker from 'components/DatePicker/DatePicker';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useCallback, useEffect, useState } from 'react';
import Input from 'components/Input/Input';
import {
  useGetCompanyForDefaultAddress,
  useGetCompanyShippingAddresses,
  useGetDeliveryDays,
} from './hooks';
import moment from 'moment';
import Loader from 'components/Loader/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import { IShippingAddress } from './types';
import { IDetailsOrderData, ShippingType } from 'types/Order.types';
import { IDeliveryDays } from 'components/DatePicker/DatePicker';
import CustomAddressCard from './CustomAddressCard/CustomAddressCard';
import { setDetailsOrderData } from 'store/Order/actions/order';
import { AppMode } from 'store/Common/reducers/common';
import Icon from 'components/Icon/Icon';
import { Asterisk } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';

// Checkout page is used in DetailsTab of SupplierNewOrderPage and BuyerNewOrderPage,
// where it reads and writes the single set of order details in orderInfo.
//
// Creating orders from a quotation needs one of these per supplier, so every
// binding to that single slot can be overridden through props. With no props it
// behaves exactly as before; pass all three to drive an isolated instance.
interface ICheckoutPageProps {
  companyId?: string;
  value?: IDetailsOrderData;
  onChange?: (detailsOrderData: IDetailsOrderData) => void;
  // Delivery days are normally derived from the shopping cart. Flows without a
  // cart (quotation -> order) resolve them themselves and pass the result in.
  deliveryDays?: IDeliveryDays | null;
}

const CheckoutPage = ({
  companyId: companyIdProp,
  value,
  onChange,
  deliveryDays: deliveryDaysProp,
}: ICheckoutPageProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isSmallTablet } = useBreakpointFlag();
  const [reference, setReference] = useState<string>('');
  const [referenceError, setReferenceError] = useState<string>('');
  const [date, setDate] = useState<moment.Moment | null>(null);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [customAddressValid, setCustomAddressValid] = useState<boolean>(false);
  const [shippingType, setShippingType] = useState<ShippingType>(
    ShippingType.DEFAULT
  );
  const [dateTouched, setDateTouched] = useState(false);

  const company = useSelector(
    (state: IRootReducerState) => state.userInfo.company
  );

  const { companyId: orderCompanyId, detailsOrderData } = useSelector(
    (state: IRootReducerState) => state.orderInfo
  );

  const companyId = companyIdProp ?? orderCompanyId;
  const details = value ?? detailsOrderData;

  const updateDetails = useCallback(
    (updatedDetails: IDetailsOrderData) => {
      if (onChange) {
        onChange(updatedDetails);
      } else {
        dispatch(setDetailsOrderData(updatedDetails));
      }
    },
    [onChange, dispatch]
  );

  const appMode: AppMode = useSelector(
    (state: IRootReducerState) => state.commonInfo.appMode
  );

  const { additionalCartDetails, shoppingCartId } = useSelector(
    (state: IRootReducerState) => state.shoppingCartInfo
  );

  const { data: shippingAddressesData, isLoading: shippingAddressesIsLoading } =
    useGetCompanyShippingAddresses(companyId, appMode);
  const resolvesOwnDeliveryDays = deliveryDaysProp !== undefined;
  const { data: cartDeliveryDays } = useGetDeliveryDays(
    shippingType,
    shoppingCartId,
    !resolvesOwnDeliveryDays && !!shoppingCartId
  );
  const deliveryDays = deliveryDaysProp ?? cartDeliveryDays;
  const { data: companyData, isLoading: companyIsLoading } =
    useGetCompanyForDefaultAddress(
      appMode === AppMode.SUPPLIER ? companyId : company?.id
    );

  const validateReference = (value: string): string => {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
      return t('Reference is invalid');
    }
    if (trimmedValue.length > 35) {
      return t("Reference can't be larger than 35 characters");
    }
    return '';
  };

  useEffect(() => {
    if (referenceError) {
      setReferenceError(validateReference(reference));
    }
  }, [i18n.language]);

  // Reset datepicker when shipping type changes
  useEffect(() => {
    setDate(null);
    setDateTouched(false);
    updateDetails({
      ...details,
      requested_delivery_date: '',
    });
  }, [shippingType]);

  useEffect(() => {
    let isValid = false;
    const hasReference = !!reference && !referenceError;
    const hasDate = !!date;

    switch (shippingType) {
      case ShippingType.CUSTOM:
        isValid = !!(customAddressValid && hasReference && hasDate);
        break;
      case ShippingType.PREDEFINED:
        isValid = !!(addressId && hasReference && hasDate);
        break;
      case ShippingType.DEFAULT:
      case ShippingType.PICKUP:
        isValid = hasReference && hasDate;
        break;
    }

    if (details.validDetails !== isValid) {
      updateDetails({
        ...details,
        validDetails: isValid,
      });
    }
  }, [
    customAddressValid,
    reference,
    referenceError,
    addressId,
    details,
    updateDetails,
    shippingType,
    date,
  ]);

  if (!companyData) {
    return (
      <Container>
        <Card>{companyIsLoading ? <Loader /> : null}</Card>
      </Container>
    );
  }

  return (
    <Container>
      {(companyIsLoading || shippingAddressesIsLoading) && <Loader />}

      <Card>
        <ShippingWrapper>
          <ShippingTitleWrapper>
            <SectionTitle>{t('Shipping address')}</SectionTitle>
            <ShippingTypesWrapper>
              {Object.values(ShippingType).map((type) => {
                if (
                  !shippingAddressesData?.shipping_addresses?.length &&
                  type === ShippingType.PREDEFINED
                ) {
                  return;
                } else {
                  return (
                    <ShippingTypeItem
                      key={type}
                      isActive={shippingType === type}
                      onClick={() => {
                        setShippingType(type);
                        updateDetails({
                          ...details,
                          shipping_type: type,
                          validDetails: false,
                        });
                      }}
                    >
                      <ShippingTypeName>{t(type)}</ShippingTypeName>
                    </ShippingTypeItem>
                  );
                }
              })}
            </ShippingTypesWrapper>
          </ShippingTitleWrapper>
          <ShippingAddress>
            {shippingType === ShippingType.PREDEFINED && (
              <>
                {shippingAddressesData?.shipping_addresses?.map(
                  (address: IShippingAddress) => (
                    <ShippingAddressCard
                      key={address.code}
                      data={address}
                      selected={addressId === address.code}
                      onClick={() => {
                        if (address.code === addressId) {
                          setAddressId(null);
                          updateDetails({
                            ...details,
                            shipping_address_code: null,
                          });
                        } else {
                          setAddressId(address.code);
                          updateDetails({
                            ...details,
                            shipping_address_code: address.code,
                          });
                        }
                      }}
                    />
                  )
                )}
              </>
            )}
            {shippingType === ShippingType.CUSTOM && (
              <CustomAddressCard
                value={details}
                onChange={updateDetails}
                setCustomAddressValid={setCustomAddressValid}
                allowedDeliveryCountries={
                  shippingAddressesData.allowed_delivery_countries
                }
              />
            )}
            {shippingType === ShippingType.PICKUP && (
              <>
                <PickupInfoNote>
                  {t('Your order will be available for pickup at factory.')}
                </PickupInfoNote>
                <PickupAddress>
                  {`(${companyData?.address?.street}, ${companyData?.address?.house_number}, ${companyData?.address?.zip_code}, ${companyData?.address?.city}, ${companyData?.address?.country})`}
                </PickupAddress>
              </>
            )}
          </ShippingAddress>
        </ShippingWrapper>

        <DeliveryDateWrapper>
          <SectionTitleWrapper>
            <SectionTitle>{t('Requested delivery date')}</SectionTitle>
            <Icon svg={Asterisk} size={18} color={COLORS.RED_100} />
          </SectionTitleWrapper>
          <DatePicker
            date={date}
            setDate={(newValue: moment.Moment | null) => {
              setDate(newValue);
              if (!newValue) {
                setDateTouched(true);
              }
              updateDetails({
                ...details,
                requested_delivery_date: newValue
                  ? newValue.format('DD-MM-YYYY')
                  : '',
              });
            }}
            width={isSmallTablet ? '100%' : ''}
            deliveryDays={deliveryDays}
            textFieldSlotProps={{
              onBlur: () => setDateTouched(true),
              error: dateTouched && !date,
              helperText:
                dateTouched && !date
                  ? t('Requested delivery date is required')
                  : undefined,
            }}
          />
        </DeliveryDateWrapper>

        <OrderReference>
          <SectionTitleWrapper>
            <SectionTitle>{t('Order reference')}</SectionTitle>
            <Icon svg={Asterisk} size={18} color={COLORS.RED_100} />
          </SectionTitleWrapper>
          <Input
            height={'42rem'}
            placeholder={t('Reference')}
            value={reference}
            width={isSmallTablet ? '100%' : '300rem'}
            wrapperStyles={{ width: isSmallTablet ? '100%' : '300rem' }}
            errorMessage={referenceError}
            onChange={(e) => {
              const value = e.target.value;
              setReference(value);
              // Clear error on change if previously had error
              if (referenceError) {
                const error = validateReference(value);
                setReferenceError(error);
              }
            }}
            onBlur={() => {
              const error = validateReference(reference);
              setReferenceError(error);
              if (!error) {
                updateDetails({
                  ...details,
                  order_reference: reference,
                });
              }
            }}
          />
        </OrderReference>

        {additionalCartDetails?.length > 0 && (
          <AdditionalCartDetails>
            <SectionTitle>{t('Additional cart details')}</SectionTitle>
            <AdditionalCartDetailsContent>
              {additionalCartDetails}
            </AdditionalCartDetailsContent>
          </AdditionalCartDetails>
        )}
      </Card>
    </Container>
  );
};

export default CheckoutPage;
